import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { navigation, getActiveNavigation } from './navigation';
import { cn } from '@tonic/shared/utils';
import { ChevronDown } from 'lucide-react';

// Remove duplicate style import
// import '../styles/styles.scss';

export function CoreLayout() {
  const location = useLocation();
  const { active, parent } = getActiveNavigation(location.pathname);
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Keep parent sections expanded based on active route
  React.useEffect(() => {
    if (parent) {
      setExpandedItems(prev => {
        const newSet = new Set(prev);
        newSet.add(parent.href);
        return newSet;
      });
    }
  }, [parent]);

  const toggleExpanded = (href) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(href)) {
        newSet.delete(href);
      } else {
        newSet.add(href);
      }
      return newSet;
    });
  };

  const isExpanded = (href) => expandedItems.has(href);

  const isActiveRoute = (itemHref) => {
    return location.pathname.startsWith(itemHref);
  };

  return (
    <div className="flex h-screen">
      {/* Sidebar */}
      <div className="w-64 bg-gray-900 text-gray-100 flex flex-col">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-gray-800">
          <Link to="/" className="text-xl font-semibold text-white no-underline">
            Pentatonic
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {navigation.map((item) => {
              const isActive = isActiveRoute(item.href);
              const isItemExpanded = isExpanded(item.href);
              const Icon = item.icon;

              return (
                <li key={item.name}>
                  <div
                    className={cn(
                      "flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors cursor-pointer",
                      isActive
                        ? "bg-indigo-600 text-white"
                        : "text-gray-300 hover:text-white hover:bg-gray-800"
                    )}
                    onClick={() => {
                      if (item.items) {
                        toggleExpanded(item.href);
                      }
                    }}
                  >
                    <Link
                      to={item.href}
                      className="flex items-center gap-3 flex-1 text-inherit no-underline hover:text-white"
                      onClick={(e) => {
                        if (item.items) {
                          e.preventDefault();
                        }
                      }}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{item.name}</span>
                    </Link>
                    {item.items && (
                      <ChevronDown 
                        className={cn(
                          "h-4 w-4 transition-transform text-inherit",
                          isItemExpanded && "transform rotate-180"
                        )} 
                      />
                    )}
                  </div>

                  {/* Subitems */}
                  {item.items && isItemExpanded && (
                    <ul className="mt-1 ml-4 pl-4 border-l border-gray-800 space-y-1">
                      {item.items.map((subItem) => {
                        const isSubActive = isActiveRoute(subItem.href);
                        const SubIcon = subItem.icon;

                        return (
                          <li key={subItem.name}>
                            <Link
                              to={subItem.href}
                              className={cn(
                                "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors no-underline",
                                isSubActive
                                  ? "bg-indigo-600 text-white"
                                  : "text-gray-300 hover:text-white hover:bg-gray-800"
                              )}
                            >
                              <SubIcon className="h-4 w-4" />
                              <span>{subItem.name}</span>
                            </Link>

                            {/* Third level items */}
                            {isSubActive && subItem.items && (
                              <ul className="mt-1 ml-4 pl-4 border-l border-gray-800 space-y-1">
                                {subItem.items.map((thirdItem) => {
                                  const isThirdActive = isActiveRoute(thirdItem.href);
                                  const ThirdIcon = thirdItem.icon;

                                  return (
                                    <li key={thirdItem.name}>
                                      <Link
                                        to={thirdItem.href}
                                        className={cn(
                                          "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors no-underline",
                                          isThirdActive
                                            ? "bg-indigo-600 text-white"
                                            : "text-gray-300 hover:text-white hover:bg-gray-800"
                                        )}
                                      >
                                        <ThirdIcon className="h-4 w-4" />
                                        <span>{thirdItem.name}</span>
                                      </Link>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto bg-white pt-16">
        <Outlet />
      </div>
    </div>
  );
} 